import { log } from "../../utils";
import { ActionType, ChatAction, ChatDetail, ChatSystemMessageDetail, SyntheticAction, isChatMsgDetail } from "../ChatAction";
import { ChatState } from "../ChatState";
import { DEFAULT_STATE } from "../DefaultState";
import connectionReducer from "./connectionReducer";
import { joinMessages } from "./joinMessages";
import { memberJoin } from "./memberJoin";
import { memberLeave } from "./memberLeave";
import { resetReducer } from "./resetReducer";

// Type guard for ChatSystemMessageDetail
function isChatSystemMessage(chat: ChatDetail): chat is ChatSystemMessageDetail {
  return chat.type === "chat.systemmessage";
}

/**
 * Find the LAST streaming message index. We must search from the end because
 * orphan isStreaming messages from interrupted streams may exist earlier in the
 * array. The active streaming message is always the most recently inserted one.
 */
function findLastStreamingIndex(chats: readonly ChatDetail[]): number {
  for (let i = chats.length - 1; i >= 0; i--) {
    const chat = chats[i];
    if (isChatMsgDetail(chat) && chat.isStreaming) {
      return i;
    }
  }
  return -1;
}

// todo: create reducer (requires redux-thunk dependency)
function appendMessageToState(state: ChatState, msg: ChatDetail) {
  state.chats = joinMessages(state.chats, msg);
  if (msg.type === "chat.msg") {
    state.activeContexts = msg.msg.context;
  }
}

function update(state = DEFAULT_STATE, action: ActionType): ChatState {
  log("action", action);

  if (action.type === "reset") {
    return resetReducer(state);
  }

  switch (action.type) {
    case "connection_update":
    case "receiveToken":
    case "sendGreeting":
      // workaround until state is split. Use combineReducers afterwards
      const newConnectionState = connectionReducer(state.connection, action);
      if (state.connection !== newConnectionState) {
        state = {
          ...state,
          lastTimestamp: action.detail?.timestamp,
          connection: newConnectionState
        }
      };

      return state;

    case "account_status":
      return {
        ...state,
        lastTimestamp: action.detail?.timestamp,
        accountStatus: action.detail.status
      };

    case "session_id":
      return {
        ...state,
        lastTimestamp: action.detail?.timestamp,
        sessionId: action.detail.sessionId
      };

    case "visual_status":
      return {
        ...state,  // Copy the existing state
        lastTimestamp: action.detail?.timestamp,  // Update lastTimestamp immutably
        visuals: {
          ...(state.visuals || {}),  // Copy existing visuals state or initialize it to an empty object if it doesn't exist
          ...(action.detail.status.drawer !== undefined && { drawer: action.detail.status.drawer }),
          ...(action.detail.status.opened !== undefined && { opened: action.detail.status.opened }),
          ...(action.detail.status.visible !== undefined && { visible: action.detail.status.visible }),
          ...(action.detail.status.hasInteracted !== undefined && { hasInteracted: action.detail.status.hasInteracted }),
        }
      };

    case "toggle_debug_mode":
      const newDebugMode = !state.debugMode;
      // Update localStorage to persist the setting
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('xaErrorOverlay', newDebugMode ? 'enabled' : 'disabled');
      }
      return {
        ...state,
        debugMode: newDebugMode
      };

    case "ws_button_display":
      return {
        ...state,
        lastTimestamp: action.detail?.timestamp,
        wsButton: {
          id: action.detail.id,
          // Preserve existing text if new text is empty (e.g., during dismiss animation)
          text: action.detail.text || state.wsButton?.text || '',
          pressBehavior: action.detail.pressBehavior,
          isPressed: action.detail.isPressed ?? false,
          isDismissing: action.detail.isDismissing ?? false
        }
      };

    case "ws_button_dismiss":
      // Only dismiss if the id matches
      if (state.wsButton?.id === action.detail.id) {
        return {
          ...state,
          lastTimestamp: action.detail?.timestamp,
          wsButton: undefined
        };
      }
      return state;

    case "department_update":
      return {
        ...state,
        lastTimestamp: action.detail?.timestamp,
        departments: {
          ...state.departments,
          [action.detail.id]: {
            ...action.detail
          }
        }
      };

    case "visitor_update":
      return {
        ...state,
        lastTimestamp: action.detail?.timestamp,
        visitor: {
          ...state.visitor,
          ...action.detail
        }
      };

    case "agent_update":
      const prevUpdateAgent = state.agents[action.detail.nick];
      return {
        ...state,
        lastTimestamp: action.detail?.timestamp,
        agents: {
          ...state.agents,
          [action.detail.nick]: {
            ...prevUpdateAgent,
            user: {
              ...prevUpdateAgent.user,
              ...action.detail,
            },
            typing: (prevUpdateAgent || { typing: false }).typing // if we had it - keep it
          }
        }
      };

    case "chat":
      const newState = {
        ...state,
        lastTimestamp: action.detail?.timestamp
      };

      switch (action.detail.type) {

        case "chat.memberjoin":
          return memberJoin(state, action.detail);

        case "chat.memberleave":
          return memberLeave(state, action.detail);

        case "chat.systemmessage":
          // Add the system message to the chat
          appendMessageToState(newState, action.detail);
          return newState;

        case "chat.systemmessagedismiss":
          // Remove system message(s) with the specified id
          const dismissId = action.detail.id;
          return {
            ...newState,
            chats: newState.chats.filter((chat) => {
              // Keep all non-system messages, and system messages that don't match the id
              if (isChatSystemMessage(chat)) {
                return chat.id !== dismissId;
              }
              return true;
            })
          };

        case "chat.queue.position":
          newState.queuePosition = action.detail.queuePosition;

          return newState;

        case "chat.request.rating":
          appendMessageToState(newState, action.detail);

          return {
            ...newState,
            lastRatingRequestTimestamp: action.detail.timestamp
          };

        case "chat.rating":
          appendMessageToState(newState, action.detail);

          return {
            ...newState,
            hasRating: !!action.detail.newRating
          };

        case "chat.file":
        case "chat.msg":
          appendMessageToState(newState, action.detail);
          const detail = action.detail;
          const msg = detail.type === "chat.msg" ? detail.msg : null;
          newState.chips = msg?.options?.length ? msg.options : [];

          return newState;

        case "chat.failureMsg":
          const agentDetails = newState.agents[action.detail.user.nick];
          let oldFailureMsg: ChatDetail;
          const retry = action.detail.failureMsg.retry;
          let finalState = newState;

          if (retry !== 1) {
            oldFailureMsg = newState.chats.filter(item => {
              return item.type === "chat.failureMsg" && item.failureMsg.retry < retry
            })[0];
            const clearedChats = newState.chats.filter(item => {
              return item !== oldFailureMsg
            });
            finalState = {
              ...newState,
              chats: clearedChats
            };
          }

          appendMessageToState(finalState, action.detail);

          return {
            ...finalState,
            agents: {
              ...finalState.agents,
              [action.detail.user.nick]: {
                ...agentDetails,
                typing: false,
                requestFailed: true
              }
            },
            failureMsg: action.detail.failureMsg
          };

        case "chat.typing":
          const agent = state.agents[action.detail.user.nick];

          return {
            ...state,
            lastTimestamp: action.detail?.timestamp,
            agents: {
              ...state.agents,
              [action.detail.user.nick]: {
                ...agent,
                typing: action.detail.typing
              }
            }
          };

        case "chat.stream.start": {
          // Clean up any orphan streaming messages from interrupted streams
          // before creating the new placeholder.
          const cleanedChats = newState.chats.map((chat) => {
            if (isChatMsgDetail(chat) && chat.isStreaming) {
              return { ...chat, isStreaming: false, wasStreamed: true };
            }
            return chat;
          });
          // Add placeholder message with streaming state.
          // Use joinMessages (insertSorted by timestamp) instead of append to
          // guarantee correct ordering even if events arrive out of order
          // (e.g., after an SSE abort/retry during tab backgrounding).
          const streamingMsg: ChatDetail = {
            type: "chat.msg",
            user: action.detail.user,
            timestamp: action.detail.timestamp,
            msg: {
              text: "",
            },
            isStreaming: true,
          };
          const newChats = joinMessages(cleanedChats, streamingMsg);
          return {
            ...newState,
            chats: newChats,
            streamingMessageId: action.detail.messageId,
          };
        }

        case "chat.stream.chunk": {
          // Find the streaming message by ID and append text
          const chunkDetail = action.detail;
          if (state.streamingMessageId === chunkDetail.messageId) {
            // Find the streaming message by ID (not by index - other messages could be inserted)
            const streamingIndex = findLastStreamingIndex(state.chats);
            if (streamingIndex >= 0) {
              const chunkText = chunkDetail.text;
              return {
                ...state,
                lastTimestamp: chunkDetail?.timestamp,
                chats: state.chats.map((chat, index) => {
                  if (index === streamingIndex && isChatMsgDetail(chat)) {
                    return {
                      ...chat,
                      msg: {
                        ...chat.msg,
                        text: (chat.msg?.text || "") + chunkText,
                      },
                    };
                  }
                  return chat;
                }),
              };
            }
          }
          return state;
        }

        case "chat.stream.tool_start": {
          // Add tool call to the streaming message (in-progress state)
          log(`Tool execution started: ${action.detail.toolName}`);
          const toolStartDetail = action.detail;
          if (state.streamingMessageId === toolStartDetail.messageId) {
            // Find the streaming message by ID (not by index - other messages could be inserted)
            const streamingIndex = findLastStreamingIndex(state.chats);
            if (streamingIndex >= 0) {
              return {
                ...state,
                lastTimestamp: toolStartDetail.timestamp,
                chats: state.chats.map((chat, index) => {
                  if (index === streamingIndex && isChatMsgDetail(chat)) {
                    const existingToolCalls = chat.toolCalls || [];
                    return {
                      ...chat,
                      toolCalls: [
                        ...existingToolCalls,
                        {
                          name: toolStartDetail.toolName,
                          toolCallId: toolStartDetail.toolCallId,
                          displayName: toolStartDetail.displayName,
                          label: toolStartDetail.label,
                          hidden: toolStartDetail.hidden,
                          input: toolStartDetail.toolInput,
                          startTime: toolStartDetail.timestamp,
                          isExecuting: true,
                        },
                      ],
                    };
                  }
                  return chat;
                }),
              };
            }
          }
          return state;
        }

        case "chat.stream.tool_end": {
          // Update tool call with result in the streaming message
          log(`Tool execution ended: ${action.detail.toolName}`);
          const toolEndDetail = action.detail;
          if (state.streamingMessageId === toolEndDetail.messageId) {
            // Find the streaming message by ID (not by index - other messages could be inserted)
            const streamingIndex = findLastStreamingIndex(state.chats);
            if (streamingIndex >= 0) {
              return {
                ...state,
                lastTimestamp: toolEndDetail.timestamp,
                chats: state.chats.map((chat, index) => {
                  if (index === streamingIndex && isChatMsgDetail(chat)) {
                    const toolCalls = chat.toolCalls?.map((tc) => {
                      // Match by toolCallId if available, otherwise fall back to name + isExecuting
                      const isMatch = toolEndDetail.toolCallId
                        ? tc.toolCallId === toolEndDetail.toolCallId
                        : tc.name === toolEndDetail.toolName && tc.isExecuting;
                      if (isMatch) {
                        return {
                          ...tc,
                          displayName: toolEndDetail.displayName || tc.displayName,
                          label: toolEndDetail.label, // Update with completion label
                          hidden: toolEndDetail.hidden ?? tc.hidden, // Preserve hidden if not specified
                          result: toolEndDetail.toolResult,
                          error: toolEndDetail.toolError,
                          endTime: toolEndDetail.timestamp,
                          isExecuting: false,
                        };
                      }
                      return tc;
                    });
                    return {
                      ...chat,
                      toolCalls,
                    };
                  }
                  return chat;
                }),
              };
            }
          }
          return state;
        }

        case "chat.stream.end": {
          // Finalize the streaming message
          const endDetail = action.detail;
          if (state.streamingMessageId === endDetail.messageId) {
            // Find the streaming message by ID (not by index - other messages could be inserted)
            const streamingIndex = findLastStreamingIndex(state.chats);
            const endStreamingChat = streamingIndex >= 0 ? state.chats[streamingIndex] : undefined;
            // Verify it's the right message using type guard
            if (streamingIndex >= 0 && endStreamingChat && isChatMsgDetail(endStreamingChat)) {
              const finalMsg = endDetail.msg;
              // Preserve tool calls from either the action or the existing message
              const toolCalls = endDetail.toolCalls || endStreamingChat.toolCalls;
              // Update chips from message options (same as chat.msg handler)
              const chips = finalMsg?.options?.length ? finalMsg.options : [];
              return {
                ...state,
                lastTimestamp: endDetail?.timestamp,
                streamingMessageId: undefined,
                chips,
                chats: state.chats.map((chat, index) => {
                  if (index === streamingIndex && isChatMsgDetail(chat)) {
                    return {
                      ...chat,
                      msg: finalMsg,
                      isStreaming: false,
                      wasStreamed: true,
                      toolCalls,
                    };
                  }
                  return chat;
                }),
              };
            }
          }
          return state;
        }

        case "chat.stream.abort": {
          // Finalize a streaming message interrupted by connection loss during sleep.
          // Keeps whatever partial text is already in the message.
          const abortDetail = action.detail;
          if (state.streamingMessageId === abortDetail.messageId) {
            const streamingIndex = findLastStreamingIndex(state.chats);
            if (streamingIndex >= 0) {
              return {
                ...state,
                lastTimestamp: abortDetail.timestamp,
                streamingMessageId: undefined,
                chats: state.chats.map((chat, index) => {
                  if (index === streamingIndex && isChatMsgDetail(chat)) {
                    return {
                      ...chat,
                      isStreaming: false,
                      wasStreamed: true,
                    };
                  }
                  return chat;
                }),
              };
            }
          }
          return state;
        }

        case "chat.stream.resume": {
          // Re-enable streaming on the last aborted bot message after replay reconnect.
          // Targets the most recent wasStreamed message that is NOT currently streaming
          // (i.e., the one finalized by chat.stream.abort). Updates streamingMessageId
          // so replayed chunks route to the existing message.
          // Guard: only resume if no stream is currently active.
          const resumeDetail = action.detail;
          if (state.streamingMessageId) {
            return state;
          }
          let lastBotIndex = -1;
          for (let i = state.chats.length - 1; i >= 0; i--) {
            const chat = state.chats[i];
            if (isChatMsgDetail(chat) && chat.wasStreamed && !chat.isStreaming) {
              lastBotIndex = i;
              break;
            }
          }
          if (lastBotIndex >= 0) {
            return {
              ...state,
              lastTimestamp: resumeDetail.timestamp,
              streamingMessageId: resumeDetail.messageId,
              chats: state.chats.map((chat, index) => {
                if (index === lastBotIndex && isChatMsgDetail(chat)) {
                  return {
                    ...chat,
                    isStreaming: true,
                    wasStreamed: false,
                  };
                }
                return chat;
              }),
            };
          }
          return state;
        }

        default:
          return state;
      }
    default:
      log("Unhandled redux action: ", action.type);
      return state;
  }
}

function mapSynthetic(state = DEFAULT_STATE, action: SyntheticAction): ChatAction {
  const newTimestamp = (action.detail?.timestamp || +new Date()) + 1; // state.lastTimestamp + 1;

  switch (action.detail.type) {
    case "write_msg":
      return {
        type: "chat",
        detail: {
          type: "chat.msg",
          user: action.detail.user,
          timestamp: newTimestamp,
          msg: action.detail.msg
        }
      };

    case "visitor.send.file":
      return {
        type: "chat",
        detail: {
          type: "chat.file",
          user: {
            displayName: state.visitor.displayName,
            nick: "visitor:",
          },
          timestamp: newTimestamp,
          attachment: action.detail.attachment
        }
      }

    default:
      throw new Error(`Unknown synthetic action. Detail type: ${(action.detail).type}`);
  }
}

export function storeHandler(state = DEFAULT_STATE, action: ActionType): ChatState {
  if (action.type === "synthetic") {
    log("synthetic action", action); // simulated action by the app
    const new_action = mapSynthetic(state, action);
    return update(state, new_action);
  } else {
    return update(state, action);
  }
}